import { IconButton, IconProps, Tab, Tabs } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles, styled, withStyles } from "@material-ui/styles";
import { useIO, useLinked, useModel } from "@type-r/react";
import { InblockIcon } from "common/invoice-aging/iconography";
import { Button, CollapsableSection, Column, Flex, Loading, div } from "lqx-react";
import React, { useEffect } from "react";
import { ExternalSystemRequest, ExternalSystemType } from "../api/externalSystemRequest";
import { BankAccount, Payment } from "../api/payment";
import { HeaderTitle } from "../components/asset-details-pane-styles";
import { CopyRow } from "../components/copy-row";
import { ActivityLog } from "../invoices/invoice-activity-logs-tab";
import { PaymentEditDTO } from "../api/paymentDTO";
import { EditPaymentTab } from "./payment-edit-tab";
import { PaymentSendToTab } from "./payment-send-to-tab";

export const PaymentDetailsPane = ({ payment, close, paymentId, $selectedTab, paymentData }: { payment: Payment, close, paymentId, $selectedTab, paymentData?}) => {
    const classes = usePaymentDetailsPaneStyles();

    const $doSendToExternalSystem = useLinked(false);
    const $setSendToExternalType = useLinked(ExternalSystemType.none);
    const externalSystemRequest = useModel(ExternalSystemRequest);
    const inblockIconClasses = useInblockIconStyles();
    const inblockedIconClases = useInblockedIconStyles();

    const assetUid = payment.uid;
    const paymentEdit = useModel(Payment);
    const paymentEditDto = useModel(PaymentEditDTO);
    const validationData = useLinked({});
    const isEdit = $selectedTab.value === 'edit';

    useEffect(() => {
        validationData.set({});
        paymentEdit.set(payment.attributes);
    }, [$selectedTab.value]);

    const save = async () => {
        const upDate = () => {
            payment.assignFrom(paymentEdit);
            $selectedTab.set('details')
        }
        await paymentEditDto.update(paymentId, { ...paymentData.attributes, dataset: paymentEdit }, upDate, validationData);
    }

    const editOpen = async () => {
        $selectedTab.set("edit")
    }

    const cancelEdit = () => {
        $selectedTab.set("details")
    }

    const isSendToExternalSystemReady = useIO(
        async () =>
            $doSendToExternalSystem.isTruthy &&
            $setSendToExternalType.value != ExternalSystemType.none &&
            await sendToExternalSystem($setSendToExternalType.value),
        [$doSendToExternalSystem.value]
    );

    const sendToExternalSystem = async (externalSystem: string) => {
        try {
            if (!payment.uid) return;

            externalSystemRequest.set({
                to: externalSystem,
                assetType: "Payment",
                assetUids: [payment.uid]
            });
            await externalSystemRequest.save();
        }
        finally {
            $doSendToExternalSystem.set(false);
        }
    };

    return isSendToExternalSystemReady ?
        <>
            <Container>
                <Header>
                    <IconButton aria-label="Close" size="small" className={classes.iconButton} onClick={() => close()}>
                        <CloseIcon />
                    </IconButton>
                    <Flex alignCenter>
                        {payment.isDigitized ?
                            <>
                                <InblockIcon src="/assets/images/inblock_logo.png" classes={inblockedIconClases} />
                                <StatusWithoutMargin>{payment.state.name}</StatusWithoutMargin>
                            </> :
                            <Status>{payment.state.name}</Status>}
                    </Flex>
                    <HeaderTitle><>Payment {payment.referenceNumber ? payment.referenceNumber : ''}</></HeaderTitle>
                </Header>
                {/* TODO add permission for this */}
                {true && (
                    <ButtonsBlock>
                        <Flex row justifyCenter>
                            {!isEdit && <EditButton onClick={editOpen} color="primary">Edit</EditButton>}
                            {isEdit && <EditButton onClick={save} color="primary">Save</EditButton>}
                            {isEdit && <EditButton onClick={cancelEdit} color="primary">Cancel</EditButton>}
                        </Flex>
                    </ButtonsBlock>
                )}
                <PaymentTabs value={$selectedTab.value}
                    variant="scrollable"
                    scrollButtons="auto"
                    onChange={(evt, newValue) => $selectedTab.set(newValue)} >
                    <PaymentTab value="details" label=" Details" disabled={isEdit} />
                    <PaymentTab value="activityLog" label="Activity Log" disabled={isEdit} />
                    {payment.isDigitized && <PaymentTab value="sendTo" label=" Send To" disabled={isEdit} />}
                </PaymentTabs>
                <PaymentTabContent>
                    {$selectedTab.value === 'details' && <Details payment={payment} paymentId={paymentId} />}
                    {$selectedTab.value === 'edit' && <EditPaymentTab payment={paymentEdit} errors={validationData.value} />}
                    {$selectedTab.value === 'activityLog' && <ActivityLog assetId={paymentId} />}
                    {$selectedTab.value === 'sendTo' && <PaymentSendToTab assetId={paymentId} assetUid={assetUid}/>}
                </PaymentTabContent>
            </Container>
        </> : <Loading />
};

const usePaymentDetailsPaneStyles = makeStyles({
    iconButton: {
        display: 'block',
        marginLeft: 'auto',
        marginTop: '10px',
        marginRight: '10px',
    },
    textAlignRight: {
        textAlign: 'right',
        paddingRight: '20px',
    }
})

const Details = ({ payment, paymentId }: { payment: Payment, paymentId }) => {
    const classes = usePaymentDetailsPaneStyles();
    const sectionStyles = SectionStyles();

    return (<>
        <CollapsableSection title="General" titleClassName={sectionStyles.sectionTitle} noBorder noShadow>
            <Column>
                <CopyRow label="Payor Name" value={payment.debtorBankAccount.payor.name} />
                <BankAccountDetails bankAccount={payment.debtorBankAccount} title="Payor Bank Account Details" />
                <CopyRow label="Payee Name" value={payment.creditorBankAccount.payee.name} />
                <BankAccountDetails bankAccount={payment.creditorBankAccount} title="Payee Bank Account Details" />
                <CopyRow label="Payment Type" value={payment.paymentType.name} />
                <CopyRow label="Reference Number" value={payment.referenceNumber} />
                <CopyRow label="Payment Amount" currency value={payment.paymentAmount} />
                <CopyRow label="Payment Details" value={payment.paymentDetails} />
                <CopyRow label="Payment Method" value={payment.paymentMethod.name} />
                <CopyRow label="Lock Box Number" value={payment.lockBoxNumber} />
                <CopyRow label="Bank Entry Date" date={!!payment.bankEntryDate} value={payment.bankEntryDate} />
                <CopyRow label="Currency" value={payment.currency} />
                <CopyRow label="Value Date" date={!!payment.assetPaymentDate} value={payment.assetPaymentDate} />
                <CopyRow label="Original Asset Obligor" value={payment.originalAssetObligor.name} />
                <CopyRow label="Asset ID" value={paymentId} />
                <CopyRow label="Asset UID" value={payment.uid} />
                <CopyRow label="Import Job ID" value={payment.importJobId} hyperlinkHref={`/import-jobs/?importJobId=${payment.importJobId}`} />
                <CopyRow label="Created Date" value={payment.createdOn} date />
                <CopyRow label="Updated Date" value={payment.updatedOn} date />
            </Column>
        </CollapsableSection>
    </>);
}

const BankAccountDetails = ({ bankAccount, title }: { bankAccount: BankAccount, title: string }) => {
    const classes = usePaymentDetailsPaneStyles();
    const sectionStyles = SectionStyles();
    return (<>
        <CollapsableSection title={title} titleClassName={sectionStyles.sectionTitle} notExpanded noBorder noShadow noTitleIndentation noMargins>
            <Column>
                <CopyRow label="Name" value={bankAccount.name} />
                <CopyRow label="Alias" value={bankAccount.alias} />
                <CopyRow label="Account Number" value={bankAccount.number} />
                <CopyRow label="Address" value={bankAccount.address} />
            </Column>
        </CollapsableSection>
    </>
    )
}

const Container = div({
    backgroundColor: 'white',
    padding: '0px',
    height: '100%',
    marginTop: '26px'
});

const Header = div({
    display: 'contents',
    width: '100%',
    height: '100%',
    border: '1px solid #E2F0F7',
    paddingTop: 0
});

const Status = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    marginLeft: '21px',
    fontWeight: 500,
    lineHeight: '15px',
});

const StatusWithoutMargin = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '15px',
});

const PaymentTabs = withStyles({
    indicator: {
        display: 'none'
    },
    root: {
        marginTop: '8px'
    }
})((props: any) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

const PaymentTab = withStyles({
    root: {
        minWidth: '73px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '3px 10px 6px',
        position: 'static',
        height: '26px',
        left: '46.58px',
        background: (props: any) => props.selected ? '#DCEEFD' : 'white',
        fontWeight: (props: any) => props.selected ? 600 : 400,
        borderRadius: '4px',
        flex: 'none',
        margin: '0px 20px',
        textTransform: 'none'
    },
})((props: any) => <Tab disableRipple {...props} />);

const PaymentTabContent = div({
    border: '1px solid #E2F0F7',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '15px',
});

const LiquidxIcon = ({ ...rest }: Partial<IconProps>) => {
    const classes = useLiquidxIconStyles(rest);
    return <img className={classes.root} src="/assets/images/liquidx.png" />
}

const useLiquidxIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const useInblockedIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '15px',
        marginRight: '2px',
        marginLeft: '21px',
    }
});

const useInblockIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const SendToButton = styled(Button)({
    minWidth: '0px',
    marginTop: '20px',
    fontSize: '12px'
});

const SendToContent = div({
    padding: '30px'
});

const SectionStyles = makeStyles({
    sectionTitle: {
        color: '#63a9dc',
        fontSize: '13px',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    },
})

const ConfirmButton = styled(Button)({
    marginBottom: '10px',
    backgroundColor: '#4197d6',
    border: '0px',
    color: 'white',
    borderRadius: '10px',
    height: '25px',
    marginLeft: 'auto',
    marginTop: '20px',
    '&:hover': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    },
    '&:active': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    },
    '&:focus': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    }
});

const EditButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

export const ButtonsBlock = div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '6px'
});